import { useCallback, useState } from "react";
import { useTicked } from "./useTicked";

/** The inbox's selection (the select-whole-list round, 2026-10-29; brief docs/briefs/select-whole-list-round.md, its last section): the ticked
 *  rows, or a second shape, "everything in this list", taken only by the bar's "Select all N" while the list holds more than the rows on screen
 *  (`total` is the list's own size from its first page). In that shape every loaded row shows ticked and the bar counts the list's total, which
 *  follows each live refetch; a tick on a row goes back to ticked rows (the loaded ones, minus that row); `clear` (Escape, the action, the list's
 *  address) drops it. The band's box (`all`) ticks the loaded rows only. */
export function useListSelection(order: string[], total: number) {
  const ticked = useTicked(order);
  const [whole, setWhole] = useState(false);
  const { all: tickAll, clear: tickClear, tick: tickOne } = ticked;
  const clear = useCallback(() => { setWhole(false); tickClear(); }, [tickClear]);
  const all = useCallback(() => { setWhole(false); tickAll(); }, [tickAll]);
  /** the bar's "Select all N": the whole list while more exists than is loaded, else the loaded rows (numbers, as before) */
  const selectAll = useCallback(() => { tickAll(); setWhole(total > order.length); }, [tickAll, total, order.length]);
  /** the 409 "changed" answer: the whole list again, with the number the fresh list names */
  const selectWhole = useCallback(() => { tickAll(); setWhole(true); }, [tickAll]);
  const tick = useCallback((id: string, on: boolean, range = false) => {
    if (whole) { setWhole(false); tickAll(); } // leaving the whole list keeps the loaded rows ticked, then this tick applies
    tickOne(id, on, range);
  }, [whole, tickAll, tickOne]);
  return {
    whole, ids: whole ? order : ticked.ids, count: whole ? total : ticked.ids.length,
    has: (id: string) => whole || ticked.has(id), tick, all, selectAll, selectWhole, clear,
  };
}
export type ListSelection = ReturnType<typeof useListSelection>;
